import {
  Unsubscribe,
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  writeBatch,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import { FirestoreProductDocument, Product } from "@dashboard/types/product";

import { readProduct, writeProduct } from "./firestore-converter/product";

export interface ProductApi {
  fetchProducts(restaurantUuid: string): Promise<Product[]>;
  subscribeToProducts(
    restaurantUuid: string,
    onChange: (products: Product[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe;
  setProduct(product: Product, isNew: boolean): Promise<void>;
  deleteProduct(restaurantUuid: string, productId: string): Promise<void>;
  bulkDeleteProducts(
    restaurantUuid: string,
    productIds: string[]
  ): Promise<void>;
}

/**
 * An implementation of the ProductApi that uses Firestore as the backend.
 */
export class FirestoreBackedProductApi implements ProductApi {
  private firestoreRefs: FirestoreRefs;

  constructor(params: { firestoreRefs: FirestoreRefs }) {
    this.firestoreRefs = params.firestoreRefs;
  }

  public fetchProducts(restaurantUuid: string): Promise<Product[]> {
    return getDocs(
      query(
        this.firestoreRefs.products(restaurantUuid),
        // createdAt is always set (for all products), and is used to order the products.
        orderBy("createdAt", "desc")
      )
    ).then((querySnapshot) =>
      querySnapshot.docs
        .map((doc) => doc.data() as FirestoreProductDocument)
        .map(readProduct)
    );
  }

  public subscribeToProducts(
    restaurantUuid: string,
    onChange: (products: Product[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      query(
        this.firestoreRefs.products(restaurantUuid),
        orderBy("createdAt", "desc")
      ),
      { includeMetadataChanges: true },
      (snapshot) => {
        // A pending write only exists in the local cache; the acked snapshot follows.
        if (snapshot.metadata.hasPendingWrites) {
          return;
        }
        onChange(
          snapshot.docs
            .map((doc) => doc.data() as FirestoreProductDocument)
            .map(readProduct)
        );
      },
      onError
    );
  }

  public setProduct(product: Product, isNew: boolean): Promise<void> {
    // The drawer holds productIds from when it was opened; a sub-product deleted
    // since then would otherwise be written back as a dangling reference.
    return runTransaction(this.firestoreRefs.firestore, async (transaction) => {
      const subProducts = await Promise.all(
        product.productIds.map((id) =>
          transaction.get(
            this.firestoreRefs.product(product.restaurantUuid, id)
          )
        )
      );
      const productIds = product.productIds.filter((_, i) =>
        subProducts[i].exists()
      );
      const firestoreProduct = writeProduct({ ...product, productIds }, isNew);
      transaction.set(
        this.firestoreRefs.product(product.restaurantUuid, product.id),
        firestoreProduct,
        { merge: true }
      );
    });
  }

  // This would be moved to an API call and handled in the backend later on
  public deleteProduct(
    restaurantUuid: string,
    productId: string
  ): Promise<void> {
    return deleteDoc(this.firestoreRefs.product(restaurantUuid, productId));
  }

  public bulkDeleteProducts(
    restaurantUuid: string,
    productIds: string[]
  ): Promise<void> {
    // Firestore has a limit of 500 operations per batch
    const BATCH_SIZE = 500;

    if (productIds.length === 0) {
      return Promise.resolve();
    }

    const db = this.firestoreRefs.firestore;

    const deletePromises: Promise<void>[] = [];

    for (let i = 0; i < productIds.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = productIds.slice(i, i + BATCH_SIZE);

      chunk.forEach((productId) => {
        const docRef = this.firestoreRefs.product(restaurantUuid, productId);
        batch.delete(docRef);
      });

      deletePromises.push(batch.commit());
    }

    return Promise.all(deletePromises).then(() => undefined);
  }
}
